import {ChevronRight, ExpandMore} from '@mui/icons-material';
import {Box, Checkbox, FormControlLabel, FormGroup, IconButton, Typography} from '@mui/material';
import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import TextAreaFormItem from './TextAreaFormItem';

const flattenVisibleTreeRows = (nodes = [], expandedMap = {}, level = 0) => {
  const result: any[] = [];
  nodes.forEach((node: any) => {
    const children = Array.isArray(node.children) ? node.children : [];
    const hasChildren = children.length > 0;
    const expanded = expandedMap[node.id] ?? level < 1;
    result.push({node, hasChildren, expanded, level});
    if (hasChildren && expanded) {
      result.push(...flattenVisibleTreeRows(children, expandedMap, level + 1));
    }
  });
  return result;
};

const flattenTreeNodes = (nodes = [], parentPath = '') => {
  const flat: any = [];
  nodes.forEach((node: any) => {
    const pathLabel = parentPath ? `${parentPath} > ${node.label}` : node.label;
    flat.push({
      id: node.id,
      label: node.label,
      pathLabel,
      children: Array.isArray(node.children) ? node.children : [],
    });
    if (Array.isArray(node.children) && node.children.length) {
      flat.push(...flattenTreeNodes(node.children, pathLabel));
    }
  });
  return flat;
};

const collectNodeAndDescendants = (node) => {
  const ids = [node.id];
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => {
      ids.push(...collectNodeAndDescendants(child));
    });
  }
  return ids;
};

const computeVisualState = (node, selectedMap) => {
  const children = Array.isArray(node.children) ? node.children : [];
  if (!children.length) {
    return {checked: Boolean(selectedMap[node.id]), indeterminate: false};
  }
  const childStates = children.map((child) => computeVisualState(child, selectedMap));
  const allChecked = childStates.every((state) => state.checked && !state.indeterminate);
  const someChecked = childStates.some((state) => state.checked || state.indeterminate);
  if (allChecked) {
    return {checked: true, indeterminate: false};
  }
  if (someChecked) {
    return {checked: false, indeterminate: true};
  }
  return {checked: Boolean(selectedMap[node.id]), indeterminate: false};
};

const reconcileSelectionMap = (nodes = [], selectedMap = {}) => {
  const nextMap = {...selectedMap};
  const walk = (node) => {
    const children = Array.isArray(node.children) ? node.children : [];
    if (!children.length) {
      return {checked: Boolean(nextMap[node.id]), indeterminate: false};
    }
    const states = children.map((child) => walk(child));
    const allChecked = states.every((state) => state.checked && !state.indeterminate);
    const someChecked = states.some((state) => state.checked || state.indeterminate);
    if (allChecked) {
      nextMap[node.id] = true;
      return {checked: true, indeterminate: false};
    }
    if (!someChecked) {
      nextMap[node.id] = false;
      return {checked: false, indeterminate: false};
    }
    nextMap[node.id] = false;
    return {checked: false, indeterminate: true};
  };

  nodes.forEach((node) => walk(node));
  return nextMap;
};

export default function MultiCheckboxTreeFormItem(props) {
  const {name, disabled, optionsTree = [], required, allowOthersOption, nameOthers, onChange, onlyOneOption} = props;
  const {setValue} = useFormContext();
  const rawValues = useWatch({name, defaultValue: []});
  const [expandedMap, setExpandedMap] = useState<any>({});
  const [focusedId, setFocusedId] = useState<any>(null);
  const checkboxRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const pendingFocusId = useRef<string | null>(null);

  const flattened = useMemo(() => flattenTreeNodes(optionsTree), [optionsTree]);
  const selectedMap = useMemo(() => {
    const map = {};
    (Array.isArray(rawValues) ? rawValues : []).forEach((entry) => {
      map[entry.id] = Boolean(entry.value);
    });
    return map;
  }, [rawValues]);

  useLayoutEffect(() => {
    if (pendingFocusId.current == null) return;
    const id = pendingFocusId.current;
    pendingFocusId.current = null;
    checkboxRefs.current[id]?.focus({preventScroll: true});
  });

  const updateFormValues = (nextSelectedMap, preserveSingleSelection = false) => {
    const normalized = preserveSingleSelection ? {...nextSelectedMap} : reconcileSelectionMap(optionsTree, nextSelectedMap);
    let values = flattened.map((node: any) => ({
      id: node.id,
      label: node.pathLabel,
      value: Boolean(normalized[node.id]),
    }));

    if (onlyOneOption && !preserveSingleSelection) {
      const firstSelectedIndex = values.findIndex((value) => value.value);
      values = values.map((value, idx) => ({
        ...value,
        value: firstSelectedIndex === -1 ? false : idx === firstSelectedIndex,
      }));
    }

    setValue(name, values, {shouldDirty: true, shouldValidate: true});
    onChange && onChange(values);
  };

  const toggleNode = (node, checked) => {
    pendingFocusId.current = node.id;
    if (onlyOneOption) {
      const nextSelectedMap = {};
      flattened.forEach((item: any) => {
        nextSelectedMap[item.id] = false;
      });
      if (checked) {
        nextSelectedMap[node.id] = true;
      }
      updateFormValues(nextSelectedMap, true);
      return;
    }

    const targetIds = collectNodeAndDescendants(node);
    const nextSelectedMap = {...selectedMap};
    targetIds.forEach((id) => {
      nextSelectedMap[id] = checked;
    });
    updateFormValues(nextSelectedMap);
  };

  const toggleExpand = (nodeId) => {
    setExpandedMap((prev) => ({...prev, [nodeId]: !prev[nodeId]}));
  };

  const visibleRows = useMemo(
    () => flattenVisibleTreeRows(optionsTree, expandedMap),
    [optionsTree, expandedMap],
  );

  const focusRow = (id) => {
    setFocusedId(id);
    requestAnimationFrame(() => checkboxRefs.current[id]?.focus());
  };

  const handleTreeKeyDown = (event) => {
    if (disabled) return;
    const target = event.target as HTMLElement;
    if (target?.tagName === 'TEXTAREA' || (target?.tagName === 'INPUT' && (target as HTMLInputElement).type === 'text')) {
      return;
    }
    const rows = visibleRows;
    if (!rows.length) return;
    const idx = rows.findIndex((row) => row.node.id === focusedId);
    const current = idx < 0 ? 0 : idx;
    const item = rows[current];

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = rows[Math.min(current + 1, rows.length - 1)];
      if (next) focusRow(next.node.id);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const next = rows[Math.max(current - 1, 0)];
      if (next) focusRow(next.node.id);
      return;
    }
    if (event.key === 'ArrowRight' && item?.hasChildren && !item.expanded) {
      event.preventDefault();
      toggleExpand(item.node.id);
      return;
    }
    if (event.key === 'ArrowLeft' && item?.hasChildren && item.expanded) {
      event.preventDefault();
      toggleExpand(item.node.id);
      return;
    }
    if (event.key === 'Enter' && item && !disabled) {
      event.preventDefault();
      pendingFocusId.current = item.node.id;
      const visual = computeVisualState(item.node, selectedMap);
      toggleNode(item.node, !visual.checked);
    }
  };

  const renderNode = (node, level = 0) => {
    const children = Array.isArray(node.children) ? node.children : [];
    const hasChildren = children.length > 0;
    const expanded = expandedMap[node.id] ?? level < 1;
    const visualState = computeVisualState(node, selectedMap);

    return (
      <Box key={node.id} sx={{pl: level * 2}}>
        <Box display="flex" alignItems="center">
          {hasChildren ? (
            <IconButton size="small" onClick={() => toggleExpand(node.id)}>
              {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          ) : (
            <Box width={30} />
          )}
          <FormControlLabel
            style={{width: 'fit-content', cursor: disabled ? 'default' : 'pointer'}}
            control={
              <Checkbox
                inputRef={(el) => {
                  checkboxRefs.current[node.id] = el;
                }}
                checked={visualState.checked}
                indeterminate={visualState.indeterminate}
                disabled={disabled}
                onFocus={() => setFocusedId(node.id)}
                onChange={(event) => toggleNode(node, Boolean(event.target.checked))}
                size="small"
              />
            }
            label={<Typography variant="body2">{node.label}</Typography>}
          />
        </Box>
        {hasChildren && expanded && <Box>{children.map((child) => renderNode(child, level + 1))}</Box>}
      </Box>
    );
  };

  const noneSelected = rawValues?.every((item) => !item.value);
  const showOthers = allowOthersOption && rawValues?.find((item) => item.id === 'others')?.value;

  return (
    <FormGroup onKeyDown={handleTreeKeyDown}>
      {optionsTree.map((node) => renderNode(node, 0))}
      {showOthers && (
        <Box sx={disabled ? {p: 1, mb: 2, border: '1px solid lightgray', borderRadius: '5px'} : undefined}>
          <TextAreaFormItem
            rows={3}
            disabled={disabled}
            name={nameOthers}
            placeholder="Si tu respuesta es otros, por favor especifica"
          />
        </Box>
      )}
      {required && noneSelected && (
        <Typography
          variant="subtitle2"
          sx={{
            color: '#FF1943',
            fontWeight: 'bold',
            fontSize: '0.9em;',
            fontFamily: 'inherit',
            letterSpacing: '0.03333em',
          }}>
          {required}
        </Typography>
      )}
    </FormGroup>
  );
}
